import React, { memo } from 'react';

import { Box, Typography } from '@rbilabs/universal-components';
import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { ScrollView, StyleSheet } from 'react-native';

import { primitive } from 'styles/constants';

import { OptionsListWrapper, StyledOptionsListItem } from './styles';
import { AutocompletePrediction } from './types';

type DropdownContentProps = {
  predictions: AutocompletePrediction[] | undefined | null;
  noPredictionsFoundText: string;
  highlightedItemId: string | null;
  onSelect: (item: AutocompletePrediction) => void;
  maskSuggestionsForSessionReplay?: boolean;
};

function AddressSuggestionText({
  mainText,
  secondaryText,
  maskForSessionReplay,
}: {
  mainText: string;
  secondaryText: string;
  maskForSessionReplay?: boolean;
}) {
  const mainTextNode = (
    <Typography fontSize="xs" bold mr="$1">
      {mainText}
    </Typography>
  );

  const secondaryTextNode = (
    <Typography fontSize="xs" numberOfLines={1}>
      {secondaryText}
    </Typography>
  );

  if (!maskForSessionReplay) {
    return (
      <>
        {mainTextNode}
        {secondaryTextNode}
      </>
    );
  }

  return (
    <>
      <DatadogSessionReplayMask>{mainTextNode}</DatadogSessionReplayMask>
      <DatadogSessionReplayMask style={styles.suggestionSecondaryMask}>
        {secondaryTextNode}
      </DatadogSessionReplayMask>
    </>
  );
}

export const DropdownContent = memo(
  ({
    predictions,
    noPredictionsFoundText,
    highlightedItemId,
    onSelect,
    maskSuggestionsForSessionReplay,
  }: DropdownContentProps) => {
    if (!predictions?.length) {
      return (
        <OptionsListWrapper>
          <StyledOptionsListItem disabled>
            <Typography fontSize="2xs">{noPredictionsFoundText}</Typography>
          </StyledOptionsListItem>
        </OptionsListWrapper>
      );
    }

    return (
      <OptionsListWrapper>
        <ScrollView
          style={styles.scrollView}
          keyboardShouldPersistTaps="always"
          nestedScrollEnabled
        >
          {predictions.map(item => (
            <StyledOptionsListItem
              key={item.placeId}
              onPress={() => onSelect(item)}
              isHighlighted={highlightedItemId === item.placeId}
            >
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="flex-start"
                testID="address-suggestion"
                alignItems="stretch"
                accessible
                accessibilityLabel={`${item.mainText} ${item.secondaryText}`}
                accessibilityRole="menuitem"
              >
                <AddressSuggestionText
                  mainText={item.mainText}
                  secondaryText={item.secondaryText}
                  maskForSessionReplay={maskSuggestionsForSessionReplay}
                />
              </Box>
            </StyledOptionsListItem>
          ))}
        </ScrollView>
      </OptionsListWrapper>
    );
  }
);

const styles = StyleSheet.create({
  scrollView: {
    borderBottomLeftRadius: primitive.$borderRadius,
    borderBottomRightRadius: primitive.$borderRadius,
    overflow: 'hidden',
  },
  suggestionSecondaryMask: {
    flexShrink: 1,
    minWidth: 0,
  },
});
